import { Inject, Injectable, InjectionToken } from "@angular/core";
import Ajv, { AnySchema } from "ajv";

export const AJV_INSTANCE = new InjectionToken<Ajv>("The AJV Class Instance");
export const AJV_CLASS = new InjectionToken<Ajv>("The AJV Class Instance");
export const AJV_CONFIG = new InjectionToken<Ajv>("The AJV Class config");

/**
 * The response of a validation result
 */
export interface ValidateResult {
  /**
   * If the result is valid or not
   */
  isValid: boolean;

  /**
   * Error text from the validator
   */
  errorsText: string;
}

@Injectable({
  providedIn: "root"
})
export class JsonSchemaValidationService {
  constructor(@Inject(AJV_INSTANCE) private readonly ajv: Ajv) {
    this.addFormats();
  }
  // private readonly ajv = new Ajv({ allErrors: true });

  /**
   * Fetches the Schema and adds it to the validator schema set
   * @param schema The schema to add if not already present
   * @param name The name of the schema, this will be used as the key to store it
   */
  public loadSchema(schema: AnySchema, name: string): void {
    if (!this.ajv.getSchema(name)) {
      this.ajv.addSchema(schema, name);
    }
  }

  /**
   * Validate data against a schema
   * @param name The name of the schema to validate
   * @param data The data to validate
   */
  public validateData<T>(name: string, data: T): ValidateResult {
    const isValid = this.ajv.validate(name, data) as boolean;
    return { isValid, errorsText: this.ajv.errorsText() };
  }

  private addFormats() {
    //DATE & TIME FORMATS
    this.ajv.addFormat("date", (data: string) => /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(data));
    this.ajv.addFormat("time", (data: string) =>
      /^([01]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])([+-][0-9]{2}:[0-9]{2}|Z)$/.test(data)
    );
    this.ajv.addFormat("date-time", (data: string) =>
      /^([0-9]{4}-[0-9]{2}-[0-9]{2})T([01]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])([+-][0-9]{2}:[0-9]{2}|Z)$/.test(
        data
      )
    );
    this.ajv.addFormat("iso-time", (data: string) =>
      /^([01]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])([+-][0-9]{2}:[0-9]{2}|Z)?$/.test(data)
    );
    this.ajv.addFormat("iso-date-time", (data: string) =>
      /^[0-9]{4}-[0-9]{2}-[0-9]{2}T([01]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])([+-][0-9]{2}:[0-9]{2}|Z)$/.test(
        data
      )
    );

    //URL FORMAT
    this.ajv.addFormat("url", (data: string) =>
      /^(?:https?|ftp):\/\/(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u{00a1}-\u{ffff}]+-)*[a-z0-9\u{00a1}-\u{ffff}]+)(?:\.(?:[a-z0-9\u{00a1}-\u{ffff}]+-)*[a-z0-9\u{00a1}-\u{ffff}]+)*(?:\.(?:[a-z\u{00a1}-\u{ffff}]{2,})))(?::\d{2,5})?(?:\/[^\s]*)?$/iu.test(
        data
      )
    );

    //EMAIL FORMAT
    this.ajv.addFormat("email", (data: string) =>
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(
        data
      )
    );

    //UUID FORMAT
    this.ajv.addFormat("uuid", (data: string) =>
      /^(?:urn:uuid:)?[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(data)
    );
  }
}
